import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../styles/HptScreens.css';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_API_URL;

const PlanificadorScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const idPlantaActiva = location.state?.id_planta || '';

    // LÓGICA DE PERMISOS DE EDICIÓN BLOQUEOS: Extraemos el rol del token
    let userRol = '';
    const token = localStorage.getItem('userToken');
    if (token) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            userRol = payload.rol || '';
        } catch(e) { console.error("Error leyendo token"); }
    }

    // Comprobamos si tiene permiso (Añadimos JEFE_TURNO y JEFE TURNO por si acaso)
    const puedeEditarDescargos = ['ADMINISTRADOR', 'GESTOR', 'SUPERVISOR', 'JEFE_TURNO'].includes(userRol.toUpperCase());

    // Permisos para cambiar estado
    const puedeCambiarEstado = ['ADMINISTRADOR', 'GESTOR', 'JEFE_TURNO'].includes(userRol.toUpperCase());
    
    // --- ESTADOS ---
    const [hptsCompletas, setHptsCompletas] = useState([]); // Todas las HPTs finalizadas
    const [descargosGuardados, setDescargosGuardados] = useState({}); // Mapa de descargos por ID de HPT
    const [fechaActual, setFechaActual] = useState(new Date()); // Fecha del calendario (por defecto HOY)
    const [loading, setLoading] = useState(true);

    // ============================================================================
    // CARGA DE DATOS INICIAL
    // ============================================================================
    useEffect(() => {
        const fetchPlanificaciones = async () => {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };

            // Si por algún motivo se pierde la referencia de sesión, lo devolvemos a resguardo
            if (!idPlantaActiva) {
                Swal.fire('Error de Sesión', 'No se ha detectado ninguna planta seleccionada.', 'error');
                navigate('/dashboard');
                return;
            }
            
            try {
                // De una sola petición traemos los datos
                const res = await axios.get(`${API_URL}/api/hpt/planificador/completadas?planta=${idPlantaActiva}`, config);
                setHptsCompletas(res.data);
            } catch (error) {
                console.error("Error al cargar planificaciones:", error);
                Swal.fire('Error', 'No se pudieron cargar las planificaciones', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchPlanificaciones();
    }, []);

    // ============================================================================
    // LÓGICA DEL CALENDARIO Y FILTRADO
    // ============================================================================
    
    // Suma o resta días saltando fines de semana
    const cambiarDia = (dias) => {
        let nuevaFecha = new Date(fechaActual);
        
        do {
            nuevaFecha.setDate(nuevaFecha.getDate() + dias);
        } while (nuevaFecha.getDay() === 0 || nuevaFecha.getDay() === 6);
        
        setFechaActual(nuevaFecha);
    };

    // Formateo de Fecha central
    const formatearFechaDisplay = (fecha) => {
        const opciones = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        return fecha.toLocaleDateString('es-ES', opciones);
    };

    // FUncion ayuda Para Extraer Fechas en Hora Local
    const obtenerFechaLocalISO = (fecha) => {
            const d = new Date(fecha);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
    };

    // Comprobar si la HPT cae dentro del día mostrado en calendario
    const comprobarEstadoPlanificacion = (hpt, fechaCalendarioObj) => {
        // Si esta cerrado no aparece
        if (hpt.estado_manual === 'CERRADO') return null;

        if (!hpt.fecha_prevista) return null;

        const diaSemanaCalendario = fechaCalendarioObj.getDay();
        if (diaSemanaCalendario === 0 || diaSemanaCalendario === 6) return null;

        // Nueva por funcion en lugar toISOString
        const fechaCalendarioStr = obtenerFechaLocalISO(fechaCalendarioObj);
        const fechaInicioStr = obtenerFechaLocalISO(hpt.fecha_prevista);
        

        // Creamos el objeto de fecha y reseteamos la hora para que la suma de días sea exacta
        const fechaInicioObj = new Date(hpt.fecha_prevista);
        fechaInicioObj.setHours(0, 0, 0, 0);
        //const fechaInicioObj = new Date(fechaInicioStr);
        const diasDuracion = parseInt(hpt.dias_previstos) || 1;

        // Calculamos fecha fin
        const fechaFinObj = new Date(fechaInicioObj);
        let diasRestantes = diasDuracion - 1;
        while (diasRestantes > 0) {
            // Sumamos un día al calendario
            fechaFinObj.setDate(fechaFinObj.getDate() + 1);
            
            // Comprobamos qué día de la semana es (0 = Domingo, 6 = Sábado)
            const diaSemana = fechaFinObj.getDay();
            
            // Si NO es sábado (6) y NO es domingo (0), contamos como día válido de trabajo
            if (diaSemana !== 0 && diaSemana !== 6) {
                diasRestantes--;
            }
        }

        //Obtenemos la fecha de fin en hora local
        const fechaFinStr = obtenerFechaLocalISO(fechaFinObj);

        // Si el día cae en el rango de trabajo activo de la HPT
        if (fechaCalendarioStr >= fechaInicioStr && fechaCalendarioStr <= fechaFinStr) {
            // Si el usuario forzó manualmente APERTURA o RENOVACION, pintamos ese valor estático
            if (hpt.estado_manual === 'APERTURA' || hpt.estado_manual === 'RENOVACION') {
                return hpt.estado_manual;
            }
            // Si no hay estado manual, ejecutamos tu cálculo automático tradicional
            return (fechaCalendarioStr === fechaInicioStr) ? 'APERTURA' : 'RENOVACION';
        }
        
        // Si no está en el rango no la muetra
        return null;
    };

    // Función para asignar color de fila según el departamento
    const getColorPorDepartamento = (grupo) => {
        if (!grupo) return 'row-default';
        const g = grupo.toUpperCase();
        
        if (g.includes('MECANICO') || g.includes('SPMecanico')) return 'row-mecanico';
        if (g.includes('CAMPO SOLAR') || g.includes('SPMecanico'))  return 'row-camposolar';
        if (g.includes('ELECTRICOS') || g.includes('SPElectrico')) return 'row-electrico';
        if (g.includes('INSTRUMENTACION') || g.includes('SPElectrico')) return 'row-instrumentacion';
        if (g.includes('QUIMICO') || g.includes('SPQuimicio')) return 'row-quimico';
        if (g.includes('Operacion') || g.includes('JEFE_TURNO')) return 'row-operacion';
        
        return 'row-default';
    };

    // Función para obtener el COLOR HEX (Para el correo electrónico)
    const getHexColorPorDepartamento = (grupo) => {
        if (!grupo) return '#ffffff';
        const g = grupo.toUpperCase();
        if (g.includes('MECANICO') || g.includes('SPMecanico')) return '#BF9000';
        if (g.includes('CAMPO SOLAR') || g.includes('SPMecanico')) return '#93C47D';
        if (g.includes('ELECTRICOS') || g.includes('SPElectrico')) return '#FFE599';
        if (g.includes('INSTRUMENTACION') || g.includes('SPElectrico')) return '#A6CAEC';
        if (g.includes('QUIMICO') || g.includes('SPQuimico')) return '#ff06b4';
        if (g.includes('Operacion') || g.includes('JEFE_TURNO')) return '#EAD1DC';
        return '#ffffff';
    };

    // Filtramos lista para solo ver la que toca en esa fecha de calendario
    const hptsDelDia = hptsCompletas.map(hpt => ({
        ...hpt,
        estado_diario: comprobarEstadoPlanificacion(hpt, fechaActual)
    })).filter(hpt => hpt.estado_diario !== null); // Quitamos las que devuelven null

    // Formateo
    const formatearLotoParaTabla = (arrLoto) => {
        if (!arrLoto || !Array.isArray(arrLoto) || arrLoto.length === 0) return '-';
        return arrLoto.map(item => {
            // Retrocompatibilidad por si hubiera algún texto viejo guardado
            if (typeof item === 'string') return item; 
            const num = item.num_consignacion ? `${item.num_consignacion}` : '';
            return `${num} `;
        }).join();
    };

    // ============================================================================
    // LÓGICA DEL PORTAPAPELES
    // ============================================================================
    const copiarAlPortapapeles = async () => {
        if (hptsDelDia.length === 0) {
            Swal.fire('Aviso', 'No hay planificaciones para este día.', 'info');
            return;
        }

        // Constructor Texto plano para la tabla
        let textoPlano = `PLANIFICACIÓN DE TRABAJOS - ${formatearFechaDisplay(fechaActual).toUpperCase()}\n\n`;
        
        // Usamos CSS inline debido a que al inyectar a HTML los correos borran los estilos.
        let htmlCorreo = `
            <div style="font-family: 'Segoe UI', Arial, sans-serif; color: #333;">
                <h2 style="color: #000000; border-bottom: 2px solid #1a237e; padding-bottom: 5px;">
                    PLANIFICACIÓN DE TRABAJOS PARA EL ${formatearFechaDisplay(fechaActual).toUpperCase()}
                </h2>
                <table style="border-collapse: collapse; width: 100%; border: 1px solid #dddddd; font-size: 13px;">
                    <thead>
                        <tr style="background-color: #ffffff; color: black; text-align: left;">
                            <th style="padding: 10px; border: 1px solid #000000;">Nº PTW</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Nº OT</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Ubicación</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Descripción del trabajo</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Activo</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Observaciones</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Descargo Eléctrico</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Bloqueo Eléctrico</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Descargo Mecánico</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Horario</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Fecha de Inicio</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Jefe Trabajo</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Recurso Prev.</th>
                            <th style="padding: 10px; border: 1px solid #000000;">Estado</th>
                        </tr>
                    </thead>
                    <tbody>
        `;

        hptsDelDia.forEach((hpt, index) => {
            // Añadimos al texto plano
            textoPlano += `[OT: ${hpt.ot_referencia}] - ${hpt.titulo} (${hpt.estado_diario})\n`;

            // Añadimos Fila coloreada.
            const bgColor = getHexColorPorDepartamento(hpt.creador_grupo);
            const statusColor = hpt.estado_diario === 'APERTURA' ? '#2e7d32' : '#ef6c00';
            
            htmlCorreo += `
                <tr style="background-color: ${bgColor};">
                    <td style="padding: 8px; border: 1px solid #000000; font-weight: bold;">${hpt.numero_ptw || '-'}</td>
                    <td style="padding: 8px; border: 1px solid #000000;">${hpt.ot_referencia}</td>
                    <td style="padding: 8px; border: 1px solid #000000;">${hpt.nombre_zona || '-'}</td>
                    <td style="padding: 8px; border: 1px solid #000000;"><strong>${hpt.titulo || '-'}</strong><br/></td>
                    <td style="padding: 8px; border: 1px solid #000000;">${hpt.codigo_activo || '-'}</td>
                    <td style="padding: 8px; border: 1px solid #000000;">${hpt.descripcion_trabajo || '-'}</td>
                    <td style="padding: 8px; border: 1px solid #000000; text-align: center;">${formatearLotoParaTabla(hpt.descargos_electricos)}</td>
                    <td style="padding: 8px; border: 1px solid #000000; text-align: center;">${formatearLotoParaTabla(hpt.bloqueos_electricos)}</td>
                    <td style="padding: 8px; border: 1px solid #000000; text-align: center;">${hpt.descargos_mecanicos?.length > 0 ? hpt.descargos_mecanicos.join(', ') : '-'}</td>
                    <td style="padding: 8px; border: 1px solid #000000;">${hpt.horario_previsto || '-'}</td>
                    <td style="padding: 8px; border: 1px solid #000000;">${hpt.fecha_prevista || '-'}</td>
                    <td style="padding: 8px; border: 1px solid #000000;">${hpt.nombre} ${hpt.apellidos}</td>
                    <td style="padding: 8px; border: 1px solid #000000;">${hpt.recurso_preventivo || '-'}</td>
                    <td style="padding: 8px; border: 1px solid #000000; color: ${statusColor}; font-weight: bold;">${hpt.estado_diario}</td>
                </tr>
            `;
        });

        // Ejecutamos la API para Portapapeles
        try {
            const blobHtml = new Blob([htmlCorreo], { type: "text/html" });
            const blobText = new Blob([textoPlano], { type: "text/plain" });

            const data = [new window.ClipboardItem({ 
                "text/html": blobHtml, 
                "text/plain": blobText 
            })];

            await navigator.clipboard.write(data);
            
            Swal.fire({
                title: '¡Tabla Copiada!',
                text: 'Abre tu correo (Outlook/Gmail) y dale a Pegar (Ctrl+V).',
                icon: 'success',
                timer: 3000,
                showConfirmButton: false
            });
        } catch (err) {
            console.error("Error al usar Clipboard API:", err);
            Swal.fire('Atención', 'Tu navegador no soporta la copia de tablas HTML, intenta usar Chrome o Edge.', 'warning');
        }
    };

    // ============================================================================
    // LÓGICA EXCELJS
    // ============================================================================
    const exportarAExcel = async () => {
        if (hptsDelDia.length === 0) {
            Swal.fire('Aviso', 'No hay planificaciones para exportar en este día.', 'info');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Planificación Diaria');

        worksheet.columns = [
            { header: 'Nº PTW', key: 'ptw', width: 12 },
            { header: 'Nº OT', key: 'ot', width: 15 },
            { header: 'Ubicación', key: 'ubicacion', width: 25 },
            { header: 'Descripción del trabajo', key: 'titulo', width: 40 },
            { header: 'Activo', key: 'activo', width: 20 },
            { header: 'Observaciones', key: 'observaciones', width: 40 },
            { header: 'Descargo Eléctrico', key: 'de', width: 25 },
            { header: 'Bloqueo Eléctrico', key: 'be', width: 25 },
            { header: 'Descargo Mecánico', key: 'dm', width: 25 },
            { header: 'Horario', key: 'horario', width: 15 },
            { header: 'Fecha de Inicio', key: 'fecha', width: 18 },
            { header: 'Jefe Trabajo', key: 'jefe', width: 30 },
            { header: 'Recurso Prev.', key: 'preventivo', width: 20 },
            { header: 'Estado', key: 'estado', width: 15 }
        ];

        // Diseño de cabecera (Azul oscuro, letras blancas)
        worksheet.getRow(1).eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A237E' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
        });

        // Insertamos Datos en celdas
        hptsDelDia.forEach(hpt => {
            const row = worksheet.addRow({
                ptw: hpt.numero_ptw || '-',
                ot: hpt.ot_referencia,
                ubicacion: hpt.nombre_zona || '-',
                titulo: hpt.titulo || '-',
                activo: hpt.codigo_activo || '-',
                observaciones: hpt.descripcion_trabajo || '-',
                de: hpt.descargos_electricos?.length > 0 ? hpt.descargos_electricos.join(', ') : '-',
                be: hpt.bloqueos_electricos?.length > 0 ? hpt.bloqueos_electricos.join(', ') : '-',
                dm: hpt.descargos_mecanicos?.length > 0 ? hpt.descargos_mecanicos.join(', ') : '-',
                horario: hpt.horario_previsto || '-',
                fecha: hpt.fecha_prevista ? new Date(hpt.fecha_prevista).toLocaleDateString('es-ES') : '-',
                jefe: `${hpt.nombre} ${hpt.apellidos}`,
                preventivo: hpt.recurso_preventivo || '-',
                estado: hpt.estado_diario
            });

            // Conversion del color que obtenemos para codigo ARGB
            let rawHex = getHexColorPorDepartamento(hpt.creador_grupo).replace('#', '');
            if(rawHex === 'ffffff') rawHex = 'ffffff'; // Dejamos blanco normal si no hay grupo
            const argbColor = rawHex === 'ffffff' ? 'FFFFFFFF' : `FF${rawHex}`;
            console.log(argbColor);
            row.eachCell((cell) => {
                // Color de fondo
                if(argbColor !== 'FFFFFFFF'){
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argbColor } };
                }
                // Alineación vertical y ajuste de texto
                cell.alignment = { vertical: 'middle', wrapText: true };
                // Bordes
                cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
            });
        });

        // Descargar archivo personalizado multiplanta
        const buffer = await workbook.xlsx.writeBuffer();
        const fechaParaNombre = fechaActual.toISOString().split('T')[0];
        saveAs(new Blob([buffer]), `Planificacion_${idPlantaActiva}_${fechaParaNombre}.xlsx`);
    };

    // ============================================================================
    // EDICIÓN DE DESCARGOS
    // ============================================================================
    const handleEditarDescargos = async (hpt) => {
        const { value: dmString } = await Swal.fire({
            title: 'Editar Descargos',
            html: `
                <div style="text-align: left; font-size: 14px; color: #333;">
                    <label style="margin-top: 15px; display: block; font-weight: bold;">Descargos Mecánicos (Texto Libre):</label>
                    <input id="swal-dm" class="swal2-input" style="margin-top: 5px; width: 90%;" placeholder="Ej: DM-26/0023, DM-26121" value="${hpt.descargos_mecanicos?.join(', ') || ''}">
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Guardar Mecánicos',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#1a237e',
            preConfirm: () => {
                const dm = document.getElementById('swal-dm').value;
                return dm;
            }
        });

        if (dmString !== undefined) {
            try {
                const token = localStorage.getItem('userToken');
                const stringToArray = (str) => str.split(',').map(s => s.trim()).filter(Boolean);
                const arrayMecanicos = stringToArray(dmString);

                const payload = {
                    descargos_electricos: hpt.descargos_electricos || [],
                    bloqueos_electricos: hpt.bloqueos_electricos || [],
                    descargos_mecanicos: arrayMecanicos
                };
                // Guardamos en el backend
                await axios.put(`${API_URL}/api/hpt/${hpt.id_hpt}/descargos`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                // Actualizamos la tabla en pantalla AL INSTANTE sin recargar
                setHptsCompletas(prev => prev.map(item => {
                    if (item.id_hpt === hpt.id_hpt) {
                        return { ...item, descargos_mecanicos: arrayMecanicos };
                    }
                    return item;
                }));
                
                // Pequeña notificación de éxito (Toast)
                Swal.fire({
                    title: '¡Guardado!',
                    icon: 'success',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });
            } catch (e) {
                console.error("Error al guardar mecánicos:", e);
                Swal.fire('Error', 'No se pudieron guardar los descargos mecánicos.', 'error');
            }
        }
    };

        // ============================================================================
    // CAMBIO MANUAL DE ESTADO DIARIO
    // ============================================================================
    const handleCambiarEstadoDiario = async (hptObj) => {
        const { value: nuevoEstado } = await Swal.fire({
            title: 'Modificar Estado de Ejecución',
            text: `Selecciona el estado definitivo para la OT: ${hptObj.ot_referencia}`,
            input: 'select',
            inputOptions: {
                '': 'Automático (Cálculo por calendario)',
                'APERTURA': 'Forzar APERTURA',
                'RENOVACION': 'Forzar RENOVACIÓN',
                'CERRADO': 'CERRADO (Quitar de la lista)'
            },
            inputValue: hptObj.estado_manual || '',
            showCancelButton: true,
            confirmButtonColor: '#1a237e',
            cancelButtonColor: '#999',
            confirmButtonText: 'Guardar Estado',
            cancelButtonText: 'Cancelar'
        });

        // Si el usuario confirma y no da a cancelar
        if (nuevoEstado !== undefined) {
            try {
                const token = localStorage.getItem('userToken');
                // Si elige "Automático", mandamos NULL para limpiar la columna
                const valorEnviar = nuevoEstado === '' ? null : nuevoEstado;

                await axios.put(`${API_URL}/api/hpt/${hptObj.id_hpt}/estado-manual`, {
                    estado_manual: valorEnviar
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // Actualizamos el estado en memoria para que la pantalla cambie al instante sin parpadear
                setHptsCompletas(prev => prev.map(item => {
                    if (item.id_hpt === hptObj.id_hpt) {
                        return { ...item, estado_manual: valorEnviar };
                    }
                    return item;
                }));

                Swal.fire({
                    title: '¡Estado guardado!',
                    icon: 'success',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });
            } catch (error) {
                console.error("Error al actualizar estado diario:", error);
                Swal.fire('Error', 'No se pudo actualizar el estado de la HPT.', 'error');
            }
        }
    };

    // ============================================================================
    // RENDER (HTML/JSX)
    // ============================================================================
    if (loading) return <div className="loading-container">Cargando planificador...</div>;

    return (
        <div className="planner-wrapper">
            
            {/* CABECERA: Título y botón volver */}
            <div className="top-header" style={{ marginBottom: '20px' }}>
                <div className="d-flex align-center">
                    <span className="ptw-label">PLANIFICADOR DIARIO</span>
                </div>
                <div className="header-actions">
                    <button className="btn-excel"onClick={exportarAExcel}>
                        📊 Exportar Excel
                    </button>
                    <button className="btn-copy-clipboard" onClick={copiarAlPortapapeles}>
                        📋 Copiar Resumen
                    </button>
                    <button onClick={() => navigate('/dashboard')} className="btn-close">Volver al Dashboard</button>
                </div>
            </div>

            {/* CONTROLES DEL CALENDARIO */}
            <div className="planner-date-control">
                <button className="btn-date-nav" onClick={() => cambiarDia(-1)} title="Día Anterior">
                    ◀
                </button>
                
                <div className="current-date-display">
                    {formatearFechaDisplay(fechaActual)}
                </div>
                
                <button className="btn-date-nav" onClick={() => cambiarDia(1)} title="Día Siguiente">
                    ▶
                </button>
            </div>

            {/* TABLA DE PLANIFICACIONES */}
            <div className="table-container-scroll">
                <table className="planner-table">
                    <thead>
                        <tr>
                            <th>Nº PTW</th>
                            <th>Nº OT</th>
                            <th>Nº ST</th>
                            <th>Ubicación</th>
                            <th>Descripción del Trabajo</th>
                            <th>Activo</th>
                            <th>Observaciones</th>
                            <th>Descargo Eléct.</th>
                            <th>Bloqueo Eléct.</th>
                            <th>Descargo Mecán.</th>
                            <th>Horario</th>
                            <th>Fecha Inicio</th>
                            <th>Jefe Trabajo</th>
                            <th>Recurso Prev.</th>
                            <th>Estado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {hptsDelDia.length > 0 ? (
                            hptsDelDia.map(hpt => (
                                <tr key={hpt.id_hpt} className={getColorPorDepartamento(hpt.creador_grupo)} onClick={() => navigate(`/detalles-hpt/${hpt.id_hpt}`)} style={{ cursor: 'pointer' }}>
                                    <td className="td-ot">{hpt.numero_ptw || '-'}</td>
                                    <td className="td-ot">{hpt.ot_referencia}</td>
                                    <td className="td-ot">{hpt.id_st || '-'}</td>
                                    <td>{hpt.nombre_zona || '-'}</td>
                                    <td className="td-title" title={hpt.titulo}>{hpt.titulo || '-'}</td>
                                    <td>{hpt.codigo_activo || '-'}</td>
                                    <td className="td-desc" title={hpt.descripcion_trabajo}>{hpt.descripcion_trabajo || '-'}</td>
                                    
                                    {/* Descargos y Bloqueos*/}
                                    <td className="text-center" title="Abre los detalles para añadir candados LOTO">
                                        {formatearLotoParaTabla(hpt.descargos_electricos)}
                                    </td>
                                    
                                    <td className="text-center" title="Abre los detalles para añadir candados LOTO">
                                        {formatearLotoParaTabla(hpt.bloqueos_electricos)}
                                    </td>
                                    
                                    <td 
                                        className={`text-center ${puedeEditarDescargos ? 'editable-cell' : ''}`} 
                                        onClick={(e) => {
                                            if (puedeEditarDescargos) {
                                                e.stopPropagation(); 
                                                handleEditarDescargos(hpt);
                                            }
                                        }}
                                        title={puedeEditarDescargos ? "Clic para editar Descargos Mecánicos" : ""}
                                    >
                                        {hpt.descargos_mecanicos?.length > 0 ? hpt.descargos_mecanicos.join(', ') : '-'}
                                    </td>
                                    
                                    <td>{hpt.horario_previsto || '-'}</td>
                                    <td>{new Date(hpt.fecha_prevista).toLocaleDateString('es-ES')}</td>
                                    <td>{hpt.nombre} {hpt.apellidos}</td>
                                    <td>{hpt.recurso_preventivo || '-'}</td> 
                                    <td 
                                        className={puedeCambiarEstado ? 'editable-cell' : ''}
                                        onClick={(e) => {
                                            if (puedeCambiarEstado) {
                                                e.stopPropagation(); 
                                                handleCambiarEstadoDiario(hpt);
                                            }
                                        }}
                                        title={puedeCambiarEstado ? "Clic para modificar o cerrar este estado" : ""}
                                    >
                                        <span className={`status-pill-planner ${hpt.estado_diario === 'APERTURA' ? 'status-apertura' : 'status-renovacion'}`}>
                                            {hpt.estado_diario}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="14" style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                                    No hay trabajos planificados finalizados (con todas las firmas) para esta fecha.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default PlanificadorScreen;